/**
 * Highlight string differences in vue-json-editor tree (view) panes during English compare.
 */

const TOKEN_RE = /\S+|\s+/g;

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

import { parseLeafPath } from './translation-json';

export { parseLeafPath };

function escapePathKey(key) {
  return String(key).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/** @param {string} path */
export function leafPathToJsonEditorPath(path) {
  const segments = parseLeafPath(path);
  if (!segments.length) return '';
  return segments
    .map(seg => {
      if (typeof seg === 'number') return `[${seg}]`;
      if (/[.[\]]/.test(seg)) return `['${escapePathKey(seg)}']`;
      return `.${seg}`;
    })
    .join('')
    .replace(/^\./, '');
}

/**
 * @param {string} text
 * @returns {string[]}
 */
function tokenizeForDiff(text) {
  return String(text ?? '').match(TOKEN_RE) || [];
}

/**
 * Mark tokens in `text` that are not part of the LCS alignment with `other`.
 * @param {string} text
 * @param {string} other
 * @returns {{ text: string, diff: boolean }[]}
 */
export function diffWordHighlightParts(text, other) {
  const a = tokenizeForDiff(text);
  const b = tokenizeForDiff(other);
  const n = a.length;
  const m = b.length;

  if (!n && !m) return [];
  if (!n) return [];
  if (!m) return a.map(t => ({ text: t, diff: /\S/.test(t) }));

  const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const parts = [];
  let i = 0;
  let j = 0;

  while (i < n && j < m) {
    if (a[i] === b[j]) {
      parts.push({ text: a[i], diff: false });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      parts.push({ text: a[i], diff: /\S/.test(a[i]) });
      i++;
    } else {
      j++;
    }
  }

  while (i < n) {
    parts.push({ text: a[i], diff: /\S/.test(a[i]) });
    i++;
  }

  return parts;
}

function renderWordDiffHtml(text, other) {
  const parts = diffWordHighlightParts(text, other);
  return parts
    .map(part =>
      part.diff
        ? `<span class="gc-json-compare-word--diff">${escapeHtml(part.text)}</span>`
        : escapeHtml(part.text)
    )
    .join('');
}

function getEditorInstance(vueJsonEditorRef) {
  return vueJsonEditorRef?.editor ?? null;
}

function getEditorContainer(editor) {
  return editor?.container ?? editor?.dom?.container ?? null;
}

export function clearJsonEditorCompareHighlights(vueJsonEditorRef) {
  const editor = getEditorInstance(vueJsonEditorRef);
  const container = getEditorContainer(editor);
  if (!container) return;

  container.querySelectorAll('[data-gc-compare-original]').forEach(el => {
    const original = el.getAttribute('data-gc-compare-original');
    if (original != null) {
      el.textContent = original;
    }
    el.removeAttribute('data-gc-compare-original');
    el.classList.remove(
      'gc-json-compare-value--diff',
      'gc-json-compare-value--missing',
      'gc-json-compare-value--extra'
    );
  });

  container
    .querySelectorAll('.gc-json-compare-row--mismatch, .gc-json-compare-row--missing, .gc-json-compare-row--extra')
    .forEach(row => {
      row.classList.remove(
        'gc-json-compare-row--mismatch',
        'gc-json-compare-row--missing',
        'gc-json-compare-row--extra'
      );
    });
}

/**
 * @param {import('vue-json-editor')} vueJsonEditorRef
 * @param {{
 *   mismatched: Map<string, { machine: string, reference: string }>,
 *   missingInMachine: Set<string>,
 *   extraInMachine: Set<string>
 * } | null} diff
 * @param {'machine'|'reference'} side
 */
function normalizeDiff(diff) {
  if (!diff) return null;
  return {
    mismatched:
      diff.mismatched instanceof Map
        ? diff.mismatched
        : new Map(diff.mismatched || []),
    missingInMachine:
      diff.missingInMachine instanceof Set
        ? diff.missingInMachine
        : new Set(diff.missingInMachine || []),
    extraInMachine:
      diff.extraInMachine instanceof Set
        ? diff.extraInMachine
        : new Set(diff.extraInMachine || [])
  };
}

export function applyJsonEditorCompareHighlights(vueJsonEditorRef, diff, side) {
  const editor = getEditorInstance(vueJsonEditorRef);
  if (!editor?.node) return;

  clearJsonEditorCompareHighlights(vueJsonEditorRef);
  const normalized = normalizeDiff(diff);
  if (!normalized) return;

  const root = editor.node;

  const highlightValue = (path, className, rowClass, html) => {
    try {
      const node = root.findNode(leafPathToJsonEditorPath(path));
      const valueEl = node?.dom?.value;
      const rowEl = node?.dom?.tr;
      if (!valueEl) return;

      if (!valueEl.getAttribute('data-gc-compare-original')) {
        valueEl.setAttribute('data-gc-compare-original', valueEl.textContent || '');
      }

      if (html != null) {
        valueEl.innerHTML = html;
        valueEl.classList.add('gc-json-compare-value--diff');
      } else {
        valueEl.classList.add(className);
      }

      if (rowEl && rowClass) {
        rowEl.classList.add(rowClass);
      }
    } catch {
      // Path not in this tree (shape mismatch).
    }
  };

  if (side === 'reference') {
    for (const path of normalized.missingInMachine) {
      highlightValue(
        path,
        'gc-json-compare-value--missing',
        'gc-json-compare-row--missing'
      );
    }
  }

  if (side === 'machine') {
    for (const path of normalized.extraInMachine) {
      highlightValue(path, 'gc-json-compare-value--extra', 'gc-json-compare-row--extra');
    }
  }

  for (const [path, pair] of normalized.mismatched.entries()) {
    const text = side === 'machine' ? pair.machine : pair.reference;
    const other = side === 'machine' ? pair.reference : pair.machine;
    const html = renderWordDiffHtml(text, other);
    highlightValue(path, 'gc-json-compare-value--diff', 'gc-json-compare-row--mismatch', html);
  }
}

/**
 * @param {import('vue-json-editor')} vueJsonEditorRef
 * @param {object | null} diff
 * @param {'machine'|'reference'} side
 * @param {number} [delayMs]
 */
export function scheduleJsonEditorCompareHighlights(
  vueJsonEditorRef,
  diff,
  side,
  delayMs = 80
) {
  return new Promise(resolve => {
    window.setTimeout(() => {
      applyJsonEditorCompareHighlights(vueJsonEditorRef, diff, side);
      resolve();
    }, delayMs);
  });
}
